import React, { useMemo, useRef, useState } from "react";
import {
  LikeIcon,
  ViewIcon,
  ReportIcon,
  NotesIcon,
  LikeModalSVG,
  ViewModalSVG,
  ReportModalSVG,
  NotesModalSVG,
} from "../components/SvgIcons";
import { useQuestionViewActivity } from "../hooks/useQuestionBankQuestionFeedback";
import "../homeDashboard/DashboardSkeleton.css";

const TABS = ["All", "Like", "View", "Report", "Notes"];

const TAB_ICONS = {
  All: null,
  Like: <LikeIcon />,
  View: <ViewIcon />,
  Report: <ReportIcon />,
  Notes: <NotesIcon />,
};

const BADGE_ICONS = {
  Like: <LikeModalSVG />,
  View: <ViewModalSVG />,
  Report: <ReportModalSVG />,
  Notes: <NotesModalSVG />,
};

const EMPTY_STATE_COPY = {
  All: {
    title: "No Activity Yet",
    description:
      "Views, likes, reports, and notes for this question will appear here once learners start interacting.",
  },
  Like: {
    title: "No Likes Yet",
    description:
      "When someone likes this question, that activity will appear here.",
  },
  View: {
    title: "No Views Yet",
    description:
      "Question views will start showing here as soon as learners open this question.",
  },
  Report: {
    title: "No Reports Yet",
    description:
      "Reported issues will show up here if any learner flags this question.",
  },
  Notes: {
    title: "No Notes Yet",
    description:
      "Personal notes saved on this question will appear here when available.",
  },
};

const FALLBACK_AVATAR =
  "https://storage.googleapis.com/vidyakasam-user/default/vidyakasam_logo.jpg";

const ViewModalShimmer = () => (
  <>
    {Array.from({ length: 4 }).map((_, index) => (
      <div className="view-item Grayborder" key={`view-shimmer-${index}`}>
        <div className="view-avatar-wrapper">
          <span className="dashboard-skeleton dashboard-skeleton--icon view-modal__shimmer-avatar" />
        </div>

        <div className="view-content">
          <div className="view-top">
            <span className="dashboard-skeleton dashboard-skeleton--line view-modal__shimmer-name" />
            <span className="dashboard-skeleton dashboard-skeleton--line view-modal__shimmer-time" />
          </div>
          <span className="dashboard-skeleton dashboard-skeleton--line view-modal__shimmer-text" />
        </div>
      </div>
    ))}
  </>
);

const ViewModal = ({ open, onClose, question = null }) => {
  const [activeTab, setActiveTab] = useState("All");
  const [dragY, setDragY] = useState(0);
  const startY = useRef(0);
  const questionId = Number(question?.id || 0);
  const viewQuery = useQuestionViewActivity(questionId, {
    enabled: open && Boolean(questionId),
  });

  const visibleEntries = useMemo(() => {
    if (activeTab === "All") {
      return viewQuery.data?.all || [];
    }

    return viewQuery.data?.[activeTab.toLowerCase()] || [];
  }, [activeTab, viewQuery.data]);
  const isViewActivityLoading =
    (viewQuery.isLoading || viewQuery.isFetching) &&
    !viewQuery.error &&
    !visibleEntries.length;

  const tabCounts = useMemo(
    () => ({
      All: Number(viewQuery.data?.totalData || 0),
      Like: Number(viewQuery.data?.totalLikes || 0),
      View: Number(viewQuery.data?.totalViews || 0),
      Report: Number(viewQuery.data?.totalReports || 0),
      Notes: Number(viewQuery.data?.totalNotes || 0),
    }),
    [viewQuery.data],
  );
  const emptyState = EMPTY_STATE_COPY[activeTab] || EMPTY_STATE_COPY.All;

  if (!open) return null;

  const isMobile = window.innerWidth <= 768;

  const handleTouchStart = (event) => {
    startY.current = event.touches[0].clientY;
  };

  const handleTouchMove = (event) => {
    const diff = event.touches[0].clientY - startY.current;
    if (diff > 0) setDragY(diff);
  };

  const handleTouchEnd = () => {
    if (dragY > 120) onClose();
    setDragY(0);
  };

  return (
    <div className="view-overlay" onClick={onClose}>
      <div
        className="view-modal"
        style={{
          transform: isMobile ? `translateY(${dragY}px)` : undefined,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="view-drag-handle" />

        <div className="view-header">
          <div className="grayText mb-0">
            <span className="fw-bold blackText me-1">
              Q.
              {String(question?.serialNo || questionId || "").padStart(2, "0")}
            </span>
            <span
              dangerouslySetInnerHTML={{
                __html:
                  question?.questionHtml ||
                  question?.question ||
                  question?.questionText ||
                  "Question activity",
              }}
            />
          </div>
          <span className="view-close" onClick={onClose}>
            ×{" "}
          </span>
        </div>

        <div className="view-tabs">
          {TABS.map((tab) => (
            <div
              key={tab}
              className={`view-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {TAB_ICONS[tab]}
              <span>
                <span className="tab-name">{tab}</span> <b>{tabCounts[tab]}</b>
              </span>
            </div>
          ))}
        </div>

        <div className="view-list">
          {isViewActivityLoading ? <ViewModalShimmer /> : null}
          {!isViewActivityLoading && viewQuery.error ? (
            <div className="choose-state__status choose-state__status--error">
              {viewQuery.error?.response?.data?.message ||
                viewQuery.error?.message ||
                "Unable to load activity."}
            </div>
          ) : null}
          {!isViewActivityLoading &&
          !viewQuery.error &&
          !visibleEntries.length ? (
            <div className="view-emptyState">
              <div className="view-emptyState__icon" aria-hidden="true">
                {TAB_ICONS[activeTab] || <ViewModalSVG />}
              </div>
              <h4 className="view-emptyState__title">{emptyState.title}</h4>
              <p className="view-emptyState__text">{emptyState.description}</p>
            </div>
          ) : null}
          {visibleEntries.map((item, index) => (
            <div className="view-item Grayborder" key={`${item.id}-${index}`}>
              <div className="view-avatar-wrapper">
                <img src={item.avatar || FALLBACK_AVATAR} alt="" />
                <span className="view-avatar-badge">
                  {BADGE_ICONS[item.type] || <ViewModalSVG />}
                </span>
              </div>

              <div className="view-content">
                <div className="view-top">
                  <h5 className="blackText fw-semibold">{item.name}</h5>
                  <span className="grayText fw-medium view-time">
                    {item.time}
                  </span>
                </div>
                <p className="grayText fw-medium">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ViewModal;
